/** Read-only Books/Desk reconciliation followed by a confirm-card import (#252). */
import { Button, Chip, Dialog, EmptyState, ErrorBanner, Input, SkeletonRows } from '@patchbay/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import * as React from 'react'
import { describeError } from '../../lib/api/client'
import { poText } from '../../lib/po'
import { type Card, type ExistingPurchaseOrderOut, procurementApi, procurementKeys } from './api'
import { ProcurementConfirmCard } from './card'
import { money } from './pricing'

function DeskComparison({ po }: { po: ExistingPurchaseOrderOut }) {
  const desk = po.desk
  if (desk.state === 'not_linked') return <p className="text-sm text-muted-foreground">No Desk Purchase Orders record is linked.</p>
  if (desk.state === 'unreachable') {
    return <ErrorBanner tone="warning" title="Desk PO comparison unavailable" detail={desk.detail ?? 'Nothing was inferred.'} />
  }
  return (
    <div className="space-y-1 rounded-md border border-border p-2.5 text-sm">
      <div className="flex items-center gap-2">
        <span className="font-medium">Desk PO {desk.record_id}</span>
        <Chip tone={desk.state === 'mismatch' ? 'warning' : 'success'}>
          {desk.state === 'mismatch' ? 'Does not match Books' : 'Matches Books'}
        </Chip>
      </div>
      <p>
        Vendor {desk.vendor_name ?? 'unavailable'} · amount {money(desk.amount ?? null, po.currency)} · status{' '}
        {desk.status ?? 'unavailable'}
      </p>
      {desk.mismatches.length ? <p className="font-medium text-warning">Different: {desk.mismatches.join(', ')}</p> : null}
    </div>
  )
}

function Preview({ po, onReview }: { po: ExistingPurchaseOrderOut; onReview: () => void }) {
  return (
    <article className="space-y-2 rounded-lg border border-border p-3">
      <header className="flex flex-wrap items-center gap-2">
        <strong>{poText(po.books_po_number)}</strong>
        <Chip tone={po.status === 'billed' || po.status === 'received' ? 'success' : 'outline'}>{po.status.replaceAll('_', ' ')}</Chip>
        {po.attached_case_id ? <Chip tone="outline">Already attached</Chip> : null}
        <span className="ml-auto tabular text-sm">{money(po.amount ?? null, po.currency)}</span>
      </header>
      <p className="text-sm">
        {po.vendor_name}
        {po.reference_number ? ` · ${po.reference_number}` : ''}
      </p>
      <ul className="text-sm">
        {po.lines.map((line) => (
          <li key={line.position}>
            {line.quantity} × {line.name}
            {line.sku ? ` · ${line.sku}` : ''} · {line.quantity_received} received
            {line.unit_cost !== undefined ? ` · cost ${money(line.unit_cost ?? null, po.currency)}` : ''}
          </li>
        ))}
      </ul>
      <DeskComparison po={po} />
      <div className="flex justify-end">
        <Button variant="primary" disabled={Boolean(po.attached_case_id)} onClick={onReview}>
          Review attach card
        </Button>
      </div>
    </article>
  )
}

export function ExistingPurchaseOrderDialog({
  caseId,
  open,
  onOpenChange,
}: {
  caseId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const qc = useQueryClient()
  const [input, setInput] = React.useState('')
  const [query, setQuery] = React.useState('')
  const [selected, setSelected] = React.useState<ExistingPurchaseOrderOut | null>(null)
  const [card, setCard] = React.useState<Card | null>(null)
  const [proposing, setProposing] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const results = useQuery({
    queryKey: procurementKeys.existingPurchaseOrders(caseId, query),
    queryFn: () => procurementApi.existingPurchaseOrders(caseId, query),
    enabled: open && card === null,
    retry: 1,
  })
  const reset = () => {
    setInput('')
    setQuery('')
    setSelected(null)
    setCard(null)
    setError(null)
  }
  const propose = async (po: ExistingPurchaseOrderOut) => {
    setSelected(po)
    setProposing(true)
    setError(null)
    try {
      setCard(await procurementApi.proposeExistingPurchaseOrder(caseId, po.books_po_id))
    } catch (cause) {
      setError(describeError(cause))
    } finally {
      setProposing(false)
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && card?.state !== 'proposed') reset()
        onOpenChange(next)
      }}
      title="Attach an existing purchase order"
      description="Search Books by PO number or Books id. Patchbay also checks this case's C-number and Desk ticket numbers, then compares the linked Desk Purchase Orders record."
    >
      {card && selected ? (
        <div className="space-y-3">
          <ProcurementConfirmCard
            card={card}
            onChange={setCard}
            onSettled={async () => {
              await qc.invalidateQueries({ queryKey: procurementKeys.case(caseId) })
              await qc.invalidateQueries({ queryKey: procurementKeys.existingPurchaseOrders(caseId, query) })
            }}
          >
            <p>
              Import {selected.books_po_number} from {selected.vendor_name}, including its received quantities and Desk comparison.
            </p>
          </ProcurementConfirmCard>
          {card.state !== 'proposed' ? <Button onClick={() => onOpenChange(false)}>Close</Button> : null}
        </div>
      ) : (
        <div className="space-y-3">
          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              setQuery(input.trim())
            }}
          >
            <Input
              aria-label="Existing PO number or Books id"
              placeholder="PO-06410 or Books id"
              value={input}
              onChange={(event) => setInput(event.currentTarget.value)}
            />
            <Button type="submit" variant="outline">
              <Search /> Search
            </Button>
          </form>
          {!query ? (
            <p className="text-xs text-muted-foreground">Showing Books POs whose reference matches a Desk ticket on this case.</p>
          ) : null}
          {results.isPending ? <SkeletonRows count={3} /> : null}
          {results.isError ? (
            <ErrorBanner
              title="Existing POs unreachable"
              detail={`${describeError(results.error)}. Nothing was attached.`}
              onRetry={() => void results.refetch()}
            />
          ) : null}
          {results.isSuccess && results.data.length === 0 ? (
            <EmptyState title="Nothing matched." detail="The Books lookup completed. Try the exact PO number or Books id." />
          ) : null}
          {results.data?.map((po) => (
            <Preview key={po.books_po_id} po={po} onReview={() => void propose(po)} />
          ))}
          {proposing ? <p className="text-sm text-muted-foreground">Preparing the confirm card…</p> : null}
          {error ? <ErrorBanner title="Attach card not created" detail={error} /> : null}
        </div>
      )}
    </Dialog>
  )
}
